import React from 'react';
import PropTypes from 'prop-types';
import { Select } from '@mui/material';

import InputLabel from '~/components/core/Atomic/InputLabel/InputLabel';
import ListItemText from '~/components/core/Atomic/ListItemText';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Checkbox from '~/components/ui/atoms/Checkbox';

import { normalizeMultiSelectValue } from '../Molecules/Fields/multiSelectValue';
import OptionChips from '../OptionChips';

import { useStyles } from '../../../assets/styles';
import styles from './MultiSelectFilter.module.scss';

function MultiSelectFilter({
  onChange,
  options,
  value,
  label,
  allOptionsLabel,
  enableAllOptionsSelection,
  withOptionChips,
  renderOption,
  renderValue,
  className,
  renderOptionDisabled = () => false,
  renderChipLabel,
}) {
  const { classes } = useStyles();

  // MUI's Select (v4 and v5 alike) can deliver a raw autofilled string even when `multiple` — see
  // normalizeMultiSelectValue; this raw Select doesn't route through TextField, so guard here.
  const normalizedValue = normalizeMultiSelectValue(value);

  const removeSingleValueFromSelectedOptions = (values, removedValue) => {
    const indexValue = values.indexOf(removedValue);
    values.splice(indexValue, 1);
    onChange([...values]);
  };

  const handleRenderValue = (selectedValues) => {
    if (renderValue) {
      return renderValue(selectedValues);
    } else {
      return withOptionChips ? '' : selectedValues.join(', ');
    }
  };

  const getChipLabels = () => {
    if (renderChipLabel) {
      return normalizedValue.map((v) => [v, renderChipLabel(v)]);
    }
    return normalizedValue.map((v) => [v, renderOption ? renderOption(v) : v]);
  };

  return (
    <div className={className ?? styles.defaultContainer}>
      <InputLabel className={classes.textFieldRow} style={{ opacity: '0.5', fontSize: '12px', width: '220px' }}>
        {label}
      </InputLabel>
      <Select
        onChange={(event) => {
          const newValues = normalizeMultiSelectValue(event.target.value);
          const val = enableAllOptionsSelection && newValues.includes(allOptionsLabel) ? [] : newValues;
          onChange(val);
        }}
        // Stated rather than inherited: a bare Select defaults to `outlined`, which would box this
        // filter and cut a notch for a label that is rendered separately as a sibling above it.
        variant="standard"
        // Inert while the variant is standard — MUI feeds this to the outlined notch only — but kept
        // so changing the variant does not leave the border running behind the text.
        label={label}
        className={classes.textFieldRow}
        style={{ width: '220px' }}
        value={normalizedValue}
        multiple
        autoWidth
        renderValue={handleRenderValue}
        MenuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
      >
        {enableAllOptionsSelection && (
          <MenuItem key="all_option" value={allOptionsLabel}>
            <Checkbox checked={normalizedValue.length === 0} />
            <ListItemText primary={allOptionsLabel} />
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option} value={option} disabled={renderOptionDisabled(option)}>
            <Checkbox checked={normalizedValue.indexOf(option) > -1} />
            <ListItemText primary={renderOption ? renderOption(option) : option} />
          </MenuItem>
        ))}
      </Select>
      {withOptionChips && (
        <OptionChips
          values={getChipLabels()}
          onChange={(removedValue) => removeSingleValueFromSelectedOptions(normalizedValue, removedValue)}
          disabledChips={options.filter((o) => renderOptionDisabled(o))}
        />
      )}
    </div>
  );
}

MultiSelectFilter.propTypes = {
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.any.isRequired,
  label: PropTypes.node.isRequired,
  allOptionsLabel: PropTypes.string,
  enableAllOptionsSelection: PropTypes.bool,
  withOptionChips: PropTypes.bool,
  renderOption: PropTypes.func,
  renderValue: PropTypes.func,
  renderOptionDisabled: PropTypes.func,
  className: PropTypes.string,
  renderChipLabel: PropTypes.func,
};

export default MultiSelectFilter;
